import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { WIZARD_SCREEN_TYPES } from '../constants'
import { buildWorkflow, DEFAULT_SECTIONS, WELCOME_DAY_SECTIONS, DEFAULT_DETAILS } from './config'
import WizardDialog from '../components/WizardDialog'
import DaycareWizardContext from './DaycareWizardContext'
import useWizardRouterHistory from '../hooks/useWizardRouterHistory'
import SectionNavigator from '../components/SectionNavigator'
import ExitWizardDialog from '../components/ExitWizardDialog'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import { formatArrayOfPetNames } from '../../../utils'
import DifferentFacilityBookingWarning from '../../Common/DifferentFacilityBookingWarning'

const DaycareWizard = ({ step = 0, open = false, onClose, updatedVisit = {} }) => {
	const [currentStep, setCurrentStep] = useState(step)
	const [WORKFLOW, setWorkFlow] = useState([])
	const [canProceedFunc, setCanProceedFunc] = useState(() => () => true)
	const [showExitWizardDialog, setShowExitWizardDialog] = useState(false)

	const { loading, update, updateAddOns, savePetSurvey, facility } = useDaycareVisit()
	const household = useSelector((state) => state.specific.households.selected)
	const { setStepInHistory, clearHistory } = useWizardRouterHistory()
	const user = useSelector((state) => state.general.user.user)

	// Context Related State -----------------------------------------------------
	const setWizardAttributeAndValues = (newValues) => {
		setWizardContextProvider({
			wizard_state: { ...newValues },
			setWizardAttributeAndValues
		})
	}

	const [wizardContextProvider, setWizardContextProvider] = useState({
		wizard_state: { ...DEFAULT_DETAILS },
		setWizardAttributeAndValues
	})

	// might need better logic here eventually if we do have to remove screens dynamically
	useEffect(() => {
		const w = buildWorkflow(updatedVisit)
		if (w.length >= WORKFLOW.length) setWorkFlow(w)
	}, [updatedVisit])

	// ---------------------------------------------------------------------------

	const REQUIRED_WORKFLOW = useMemo(() => {
		const { wizard_state } = wizardContextProvider
		const reqWorkflow = []

		for (const step of WORKFLOW) {
			const {
				display_step_condition,
				requires_multiple_views,
				create_multiple_views_from,
				individual_props_for_each_view_component = [],
				specific_view_in_multiple_views_not_required_when
			} = step
			if (!display_step_condition || display_step_condition(wizard_state)) {
				if (requires_multiple_views) {
					const values = wizard_state[create_multiple_views_from] // e.g wizard_state['pets']
					if (values && values.length > 0) {
						if (!requires_multiple_views(wizard_state)) {
							// if we have defined that multiple views are required, but
							// a value in state signals we don't need mulitiple, such as copy all being set to true
							// we still need to show the first view
							const singleStep = { ...step }
							// copy the individual props from individual_props_for_each_view_component array into the step
							// so the view can use them.
							individual_props_for_each_view_component.forEach((attr) => {
								singleStep[attr] = values[0][attr] // e.g. singleStep['_id'] = pets[0]['_id']
							})
							singleStep['position'] = -1
							reqWorkflow.push(singleStep)
						} else {
							// if multiple views are required, we will create new steps
							// based off the step in the workflow with the other props

							// e.g. for pet of pets
							let position = -1
							for (const value of values) {
								const nthStep = { ...step }

								// we may need multiple views, but sometimes one of the multiple views
								// does not need to be shown. This condition checks for that
								// and does not create the view if true
								if (specific_view_in_multiple_views_not_required_when !== undefined) {
									const [key, testFunction] = specific_view_in_multiple_views_not_required_when
									const testValue = value[key] // e.g pet['medication]
									if (testValue && testFunction(testValue)) {
										// e.g. () => pet[medication].length === 0
										continue
									}
								}
								// copy the individual props from individual_props_for_each_view_component array into the step
								// so the view can use them.
								individual_props_for_each_view_component.forEach((attr) => {
									nthStep[attr] = value[attr]
								})

								// Position helps us know when we have the first view in the series
								// This is used in currentComponent to pass the 'first_view_of_multiple' prop
								nthStep['position'] = position
								position++

								reqWorkflow.push(nthStep)
							}
						}
					}
				} else {
					if (step.pet_id) {
						reqWorkflow.push({
							...step,
							pet: step.pet_id
						})
					} else {
						reqWorkflow.push(step)
					}
				}
			}
		}

		return reqWorkflow
	}, [wizardContextProvider])

	const requiredSteps = useMemo(() => REQUIRED_WORKFLOW.length, [REQUIRED_WORKFLOW])

	const needsWelcomeSurvey = useMemo(() => {
		const welcome_pets = updatedVisit?.pets?.filter((p) => {
			return updatedVisit.welcome_day_pets?.includes(p._id) && !p?.welcome_day_survey_answers?.length
		})
		return welcome_pets || []
	}, [updatedVisit])

	useEffect(() => {
		const { wizard_state, setWizardAttributeAndValues } = wizardContextProvider

		if (updatedVisit) {
			const updatedVisitValues = { ...wizard_state }
			for (const key in updatedVisit) {
				if (updatedVisitValues[key] !== undefined) {
					updatedVisitValues[key] = updatedVisit[key]
				}

				if (key === 'pets') {
					const pets = updatedVisit[key]
					if (pets && pets.length > 0) {
						const petNames = pets.map(({ name }) => name)
						updatedVisitValues['petNames'] = petNames

						let activeMeds = []
						pets.forEach(({ medication }) => {
							if (medication?.length > 0) {
								activeMeds = [...activeMeds, ...medication.filter((m) => m.active !== false)]
							}
						})

						updatedVisitValues['medications'] = activeMeds
					}
				}

				if (key === 'welcome_day_pets') {
					const notWelcomeDay = updatedVisit?.pets.filter(
						(p) => !updatedVisit?.welcome_day_pets?.includes(p._id)
					)
					updatedVisitValues['pets_allowed_addons'] = notWelcomeDay
					updatedVisitValues['needs_survey'] = needsWelcomeSurvey
				}
			}

			setWizardAttributeAndValues(updatedVisitValues)
		}
	}, [updatedVisit])

	useEffect(() => {
		const stepInt = parseInt(step, 10)
		setCurrentStep(stepInt)
	}, [step])

	const userFirstName = useMemo(() => {
		if (!user || !user.displayName) {
			return 'WhoAreYou?'
		}

		const [first] = user.displayName.split(' ')
		return first || 'Skipper'
	}, [user])

	const handleCloseRequest = useCallback(() => {
		setShowExitWizardDialog(true)
	}, [setShowExitWizardDialog])

	const handleCloseRequestCancel = useCallback(() => {
		setShowExitWizardDialog(false)
	}, [setShowExitWizardDialog])

	const handleStart = useCallback(() => {
		setCurrentStep(1)
		setStepInHistory(1)
	}, [setCurrentStep, setStepInHistory])

	const handleExit = useCallback(() => {
		if (onClose) {
			onClose()
		}
	}, [onClose])

	const handleOnClose = useCallback(() => {
		if (onClose && !loading) {
			setShowExitWizardDialog(false)
			clearHistory()
			onClose()
		}
	}, [onClose, loading, setShowExitWizardDialog, clearHistory, update])

	const canProceed = useMemo(() => {
		return canProceedFunc(wizardContextProvider.wizard_state)
	}, [canProceedFunc, wizardContextProvider])

	/**
	 * returns the configuration object for the current step
	 */
	const currentWorflowStepConfig = useMemo(() => {
		return (
			REQUIRED_WORKFLOW[currentStep] || {
				section: null,
				type: null,
				component: null
			}
		)
	}, [REQUIRED_WORKFLOW, currentStep])

	const hideActionsOtherThanClose = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Start || type === WIZARD_SCREEN_TYPES.Exit) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const hideNext = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Invoice || type === WIZARD_SCREEN_TYPES.Final) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const currentSection = useMemo(() => {
		const { section, type } = currentWorflowStepConfig
		if (type && type === WIZARD_SCREEN_TYPES.Intro) {
			return null
		}

		return section
	}, [currentWorflowStepConfig])

	const currentAttribute = useMemo(() => {
		const { attr } = currentWorflowStepConfig || ''
		return attr
	}, [currentWorflowStepConfig])

	const requiresApiUpdate = useMemo(() => {
		const { requires_api_update } = currentWorflowStepConfig || false
		return requires_api_update
	}, [currentWorflowStepConfig])

	const handleUpdate = useCallback(async () => {
		const { wizard_state } = wizardContextProvider
		const {
			_id: visitId,
			copy_food_addons_to_all_pets,
			copy_other_addons_to_all_pets,
			pets,
			early_dropoff,
			extended_pickup
		} = wizard_state
		const value = wizard_state[currentAttribute]
		if (value !== undefined && requiresApiUpdate) {
			switch (currentAttribute) {
				case 'welcome_day_survey':
					await savePetSurvey(wizard_state.welcome_day_survey)
					break
				case 'early_dropoff':
					await update({ visitId, updates: { [currentAttribute]: value, extended_pickup } })
					break
				case 'extended_pickup':
					await update({
						visitId,
						updates: { [currentAttribute]: value, early_dropoff }
					})
					break
				case 'add_on_services':
					let baseValues = [...value]

					// THERE IS A LOGIC LIMITATION FOR BOTH MEAL AND ADDON COPY ALL LOGIC
					// If the user selected copy to all pets, then the logic
					// works fine. If a user goes back and unchecks the copy to all
					// and changes an individual dog's schedule, the logic will save ok as well.
					// However, if the user then goes back and re-checks copy all again,
					// the logic will not change any pets who have meals and or other addons on already.
					// @TODO if this is an issue, we can use another method to
					// count the number of meals and other enrichments each dog has and reconcile.

					if (copy_food_addons_to_all_pets === true) {
						const existingMealPetIdSet = new Set()
						const missingMealPetIds = new Set()

						const mealAddons = value.filter(({ category }) => category === 'meal')

						// Here is where the logic limitation is for the above scenario
						mealAddons.forEach(({ pet_id }) => {
							existingMealPetIdSet.add(pet_id)
						})

						pets.forEach(({ _id }) => {
							if (!existingMealPetIdSet.has(_id)) {
								missingMealPetIds.add(_id)
							}
						})

						if (missingMealPetIds.size > 0) {
							for (const pet_id of missingMealPetIds.values()) {
								const newMealAddons = mealAddons.map((addon) => {
									return { ...addon, pet_id }
								})
								baseValues = [...baseValues, ...newMealAddons]
							}
						}
					}

					if (copy_other_addons_to_all_pets === true) {
						const existingAddonPetIdSet = new Set()
						const missingAddonPetIds = new Set()

						const otherAddons = value.filter(({ category }) => category !== 'meal')

						// Here is where the logic limitation is for the above scenario
						otherAddons.forEach(({ pet_id }) => {
							existingAddonPetIdSet.add(pet_id)
						})

						pets.forEach(({ _id }) => {
							if (!existingAddonPetIdSet.has(_id)) {
								missingAddonPetIds.add(_id)
							}
						})

						if (missingAddonPetIds.size > 0) {
							for (const pet_id of missingAddonPetIds.values()) {
								const newOtherAddons = otherAddons.map((addon) => {
									return { ...addon, pet_id }
								})
								baseValues = [...baseValues, ...newOtherAddons]
							}
						}
					}

					const petIds = pets.map(({ _id }) => _id)
					await updateAddOns({ visitId, addons: baseValues, petIds })
					break
				default:
					await update({ visitId, updates: { [currentAttribute]: value } })
					break
			}
		}
	}, [wizardContextProvider, currentAttribute, requiresApiUpdate, update])

	const incrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== requiredSteps) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep + params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, requiredSteps, handleUpdate]
	)

	const decrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== 0) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep - params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, handleUpdate]
	)

	const isWelcomeDay = useMemo(() => {
		if (updatedVisit?.welcome_day_pets?.length) return true
		return false
	}, [updatedVisit])

	const currentComponent = useMemo(() => {
		const {
			component,
			can_proceed_condition,
			attr,
			type,
			individual_props_for_each_view_component,
			copy_to_all_attr,
			position
		} = currentWorflowStepConfig
		if (!component) {
			return <div>I dont seem to have that step!</div>
		}

		if (can_proceed_condition) {
			setCanProceedFunc(() => can_proceed_condition)
		} else {
			setCanProceedFunc(() => () => true)
		}

		const props = {}
		if (type === WIZARD_SCREEN_TYPES.Start) {
			props['onAction'] = handleStart
		}
		if (type === WIZARD_SCREEN_TYPES.Exit) {
			props['onAction'] = handleExit
		}
		if (attr) {
			props['attr'] = attr
		}
		if (copy_to_all_attr) {
			props['copy_to_all_attr'] = copy_to_all_attr
		}
		if (type === WIZARD_SCREEN_TYPES.Invoice || type === WIZARD_SCREEN_TYPES.Final) {
			// The invoice view is the last screen and the onComplete
			// is called after the booking is confirmed.
			props['onComplete'] = incrementStep
		}
		if (individual_props_for_each_view_component) {
			individual_props_for_each_view_component.forEach((key) => {
				props[key] = currentWorflowStepConfig[key]
			})
		}
		if (position && position === -1) {
			props['first_view_of_multiple'] = true
		}
		props['petNames'] = formatArrayOfPetNames(wizardContextProvider.wizard_state.petNames) || 'doggos'
		props['userName'] = userFirstName

		if (isWelcomeDay) {
			props.isWelcomeDay = true
			props.welcome_day_pets = updatedVisit.welcome_day_pets
			if (
				updatedVisit.pets.length > 1 &&
				updatedVisit.welcome_day_pets.length !== needsWelcomeSurvey.length
			) {
				let noSurvey = updatedVisit.pets.filter((p) => !needsWelcomeSurvey.find((pet) => pet._id == p._id))

				let noNames = noSurvey.map((n) => n.name)
				let names = needsWelcomeSurvey.map((n) => n.name)

				props.mixedText = `Get started booking a daycare visit for ${formatArrayOfPetNames(
					wizardContextProvider.wizard_state.petNames
				)}. We'll also go through some Welcome Day questions for ${formatArrayOfPetNames(names)}.`
			}
		}

		return component(props)
	}, [currentWorflowStepConfig, userFirstName, wizardContextProvider, handleStart, handleExit])

	return (
		<DaycareWizardContext.Provider value={{ wizardContextProvider }}>
			<div>
				<WizardDialog
					open={open}
					hideBack={hideActionsOtherThanClose}
					hideFooter={hideActionsOtherThanClose || hideNext}
					disableNext={!canProceed || loading}
					disableBack={loading}
					onClose={handleCloseRequest}
					onNext={incrementStep}
					onBack={decrementStep}
					loading={loading}>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							flex: 1,
							gap: '1rem',
							opacity: loading ? 0.5 : 1
						}}>
						<DifferentFacilityBookingWarning
							facility={facility}
							defaultFacilityId={household?.default_facility_id}
						/>
						{currentSection ? (
							<SectionNavigator
								sections={needsWelcomeSurvey?.length ? WELCOME_DAY_SECTIONS : DEFAULT_SECTIONS}
								activeSectionId={currentSection}
							/>
						) : null}
						{currentComponent}
					</div>
				</WizardDialog>
				<ExitWizardDialog
					title="Exit Wizard?"
					description="Are you sure you want to exit the wizard? You may never be able to return!"
					open={showExitWizardDialog}
					onExit={handleOnClose}
					onCancel={handleCloseRequestCancel}
				/>
			</div>
		</DaycareWizardContext.Provider>
	)
}

export default DaycareWizard
